import { Formik, Form, Field, ErrorMessage } from "formik"
import PrimaryModal from "../UI/Modal/PrimaryModal"
import { FormikInput, FormikTextarea, FormikSelect, PrimaryButton, SecondaryButton } from "../UI";
import { useState } from "react"
import { toast } from "react-toastify"
import { useUserContext } from "../../contexts/UserContext"
import { createPlan, updatePlan } from "../../utils/APIs"
import { planSchema } from "../../schemas"
import { useContentfulContext } from "../../contexts/ContentfulContext"

const PlanModal = ({ setSelectedAction, selectedPlan, getPlans, orgLevels }) => {
    const filteredLevels = orgLevels?.filter(level=>level.id !== "0")
    const [submitError, setSubmitError] = useState("")
    const { content } = useContentfulContext();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { userData } = useUserContext()
    const isEdit = Boolean(selectedPlan)

    // Set initial values based on whether we're editing or creating
    const initialValues = {
        plan_name: selectedPlan?.plan_name || "",
        plan_description: selectedPlan?.plan_description || "",
        org_level: selectedPlan?.org_level ?? 0,
        price: selectedPlan?.price,
        price_currency: selectedPlan?.price_currency || "USD",
        discount: selectedPlan?.discount || "",
        duration: selectedPlan?.duration || "",
        is_custom_plan: selectedPlan?.is_custom_plan || false,
        is_topup: selectedPlan?.is_topup || false,
        ai_credits_limit: selectedPlan?.ai_credits_limit ?? 0,
        ai_margin_percent: selectedPlan?.ai_margin_percent ?? 0,
    }

    const addPlan = async (payload, actions) => {
        try {
            const result = await createPlan(userData?.token, userData?.selectedOrganization?.org_token, payload)
            if (result.status) {
                toast.success(result.message)
                getPlans()
                actions.resetForm()
                setSubmitError("")
                setSelectedAction("")
            }
        } catch (error) {
            const errorMessage = error?.message || "Failed to create the plan. Please try again.";
            setSubmitError(errorMessage);
            toast.error(errorMessage);
            console.log("Create plan error:", error);
        } finally {
            setIsSubmitting(false);
        }
    }

    const editPlan = async (payload, actions) => {
        try {
            const result = await updatePlan(userData?.token, userData?.selectedOrganization?.org_token, selectedPlan._id, payload)
            if (result.status) {
                toast.success(result.message)
                getPlans()
                actions.resetForm()
                setSubmitError("")
                setSelectedAction("")
            }
        } catch (error) {
            console.log(error?.response?.data?.message)
            const errorMessage = error?.response?.data?.message || "Failed to update the plan. Please try again.";
            setSubmitError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleSubmit = async (values, actions) => {
        setIsSubmitting(true);
        const payload = {
            ...values,
            price: Number.parseFloat(values.price),
            discount: values.discount ? Number.parseFloat(values.discount) : 0,
            duration: values.is_topup ? 0 : Number.parseInt(values.duration, 10),
            org_level: values.is_topup ? "0" : values.org_level,
            ai_credits_limit: values.ai_credits_limit ? Number.parseInt(values.ai_credits_limit, 10) : 0,
            ai_margin_percent: values.is_topup ? 0 : Number.parseFloat(values.ai_margin_percent),
        }

        if (isEdit) {
            editPlan(payload, actions)
        } else {
            addPlan(payload, actions)
        }
    }

    return (
        <PrimaryModal title={isEdit ? "Edit Plan" : "Create New Plan"} hideModal={() => setSelectedAction("")}>
            <Formik
                initialValues={initialValues}
                validationSchema={planSchema}
                onSubmit={handleSubmit}
                validateOnBlur
                validateOnChange
            >
                {({ values, setFieldValue }) => (
                    <Form className="space-y-4 p-6">
                        {/* Plan Name Field */}
                        <FormikInput
                            type="text"
                            id="plan_name"
                            name="plan_name"
                            label={content?.ADMIN_PLAN?.modal.name_label}
                            placeholder="Enter plan name"
                        />

                        {/* Plan Description Field */}
                        <FormikTextarea
                            id="plan_description"
                            name="plan_description"
                            label={content?.ADMIN_PLAN?.modal.description_label}
                            placeholder="Enter plan description"
                            rows={3}
                        />

                        {/* Organization Level Field - Dropdown */}
                        {!values.is_topup && (
                            <FormikSelect
                                id="org_level"
                                name="org_level"
                                label={content?.ADMIN_PLAN?.modal.org_level_label}
                                placeholder={content?.ADMIN_PLAN?.modal.select_org}
                                options={filteredLevels?.map((level) => ({
                                    value: level.id,
                                    label: String(level.id)
                                })) || []}
                            />
                        )}

                        {/* Price Field */}
                        <FormikInput
                            type="number"
                            id="price"
                            name="price"
                            label={content?.ADMIN_PLAN?.modal.pricing_label}
                            placeholder="0"
                            min="0"
                            step="0.01"
                        />

                        {/* Price Currency Field */}
                        <FormikSelect
                            id="price_currency"
                            name="price_currency"
                            label={content?.ADMIN_PLAN?.modal.currency_label}
                            options={[
                                { value: "USD", label: "USD" },
                                { value: "EUR", label: "EUR" },
                                { value: "GBP", label: "GBP" },
                                { value: "INR", label: "INR" },
                                { value: "JPY", label: "JPY" }
                            ]}
                        />

                        {/* Discount Field */}
                        <FormikInput
                            type="number"
                            id="discount"
                            name="discount"
                            label={`${content?.ADMIN_PLAN?.modal.discount_label} (%)`}
                            placeholder="0"
                            min="0"
                            max="100"
                        />

                        {/* Duration Field */}
                        {!values.is_topup && (
                            <FormikInput
                                type="number"
                                id="duration"
                                name="duration"
                                label={content?.ADMIN_PLAN?.modal.duration_label}
                                placeholder="30"
                                min="1"
                            />
                        )}

                        {/* AI Credits Limit Field */}
                        <FormikInput
                            type="number"
                            id="ai_credits_limit"
                            name="ai_credits_limit"
                            label={content?.ADMIN_PLAN?.modal.ai_credits_limit_label}
                            placeholder="0"
                            min="0"
                        />

                        {/* AI Margin Percent Field */}
                        {!values.is_topup && (
                            <FormikInput
                                type="number"
                                id="ai_margin_percent"
                                name="ai_margin_percent"
                                label={content?.ADMIN_PLAN?.modal.ai_margin_percent_label}
                                placeholder="0"
                                min="0"
                                max="100"
                                step="0.01"
                            />
                        )}

                        {/* Is Popular Field - Radio Buttons */}
                        <div className="mb-4">
                            <span className="block text-sm font-medium text-gray-700 mb-2">{content?.ADMIN_PLAN?.modal.is_custom_plan_label}</span>
                            <div className="flex space-x-6">
                                <div className="flex items-center">
                                    <Field
                                        type="radio"
                                        id="is_custom_plan_true"
                                        name="is_custom_plan"
                                        value="true"
                                        checked={values.is_custom_plan === true}
                                        disabled={values.is_topup}
                                        onChange={() => {
                                            setFieldValue("is_custom_plan", true)
                                            setFieldValue("is_topup", false)
                                        }}
                                        className="h-4 w-4 text-primary focus:ring-primary-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <label htmlFor="is_custom_plan_true" className={`ml-2 block text-sm text-gray-700 ${values.is_topup ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        Yes
                                    </label>
                                </div>
                                <div className="flex items-center">
                                    <Field
                                        type="radio"
                                        id="is_custom_plan_false"
                                        name="is_custom_plan"
                                        value="false"
                                        checked={values.is_custom_plan === false}
                                        onChange={() => setFieldValue("is_custom_plan", false)}
                                        className="h-4 w-4 text-primary focus:ring-primary-2 border-gray-300"
                                    />
                                    <label htmlFor="is_custom_plan_false" className="ml-2 block text-sm text-gray-700">
                                        No
                                    </label>
                                </div>
                            </div>
                            <ErrorMessage name="is_custom_plan" component="p" className="mt-2 text-sm text-red-500" />
                        </div>

                        {/* Is Topup Field */}
                        <div className="mb-4">
                            <label htmlFor="is_topup" className="block text-sm font-medium text-gray-700 mb-1">
                                {content?.ADMIN_PLAN?.modal.is_topup_label}
                            </label>
                            <div className="flex space-x-4">
                                <div className="flex items-center">
                                    <Field
                                        type="radio"
                                        id="is_topup_true"
                                        name="is_topup"
                                        value="true"
                                        checked={values.is_topup === true}
                                        disabled={values.is_custom_plan}
                                        onChange={() => {
                                            setFieldValue("is_topup", true)
                                            setFieldValue("ai_margin_percent", 0);
                                            setFieldValue("is_custom_plan", false)
                                        }}
                                        className="h-4 w-4 text-primary focus:ring-primary-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <label htmlFor="is_topup_true" className={`ml-2 block text-sm text-gray-700 ${values.is_custom_plan ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        Yes
                                    </label>
                                </div>
                                <div className="flex items-center">
                                    <Field
                                        type="radio"
                                        id="is_topup_false"
                                        name="is_topup"
                                        value="false"
                                        checked={values.is_topup === false}
                                        onChange={() => {
                                            setFieldValue("is_topup", false)
                                            setFieldValue("ai_margin_percent", 0);
                                        }
                                        }
                                        className="h-4 w-4 text-primary focus:ring-primary-2 border-gray-300"
                                    />
                                    <label htmlFor="is_topup_false" className="ml-2 block text-sm text-gray-700">
                                        No
                                    </label>
                                </div>
                            </div>
                            <ErrorMessage name="is_topup" component="p" className="mt-2 text-sm text-red-500" />
                        </div>

                        {/* Submit Error */}
                        {submitError && <div className="text-red-500 text-sm mb-4">{submitError}</div>}

                        {/* Form Actions */}
                        <div className="flex justify-end space-x-4">
                            <SecondaryButton type="button" handleClick={() => setSelectedAction("")}>
                                {content?.ADMIN_PLAN?.modal.cancel}
                            </SecondaryButton>
                            <PrimaryButton type="submit" disabled={isSubmitting}>
                                {isSubmitting ? "Submitting..." : isEdit ? content?.ADMIN_PLAN?.modal.update_plan : content?.ADMIN_PLAN?.modal.create_plan}
                            </PrimaryButton>
                        </div>
                    </Form>
                )}
            </Formik>
        </PrimaryModal>
    )
}

export default PlanModal